import { useRef, type KeyboardEvent } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BotonPreguntaleAtlas } from "../../chat-atlas";
import { BannerVistaEjemplo } from "./components/BannerVistaEjemplo";
import {
  TABS_ANALISIS,
  tabActivaSegunRuta,
  tabPorDesplazamiento,
  type TabAnalisis,
} from "./tabsAnalisis";

/**
 * "Análisis de datos": la pantalla que contiene Insights y Estrategia de
 * contenido como dos pestañas. Es una ruta de layout SIN path propio; sus
 * hijas son las rutas de siempre (`insights` y `estrategia`), así que la
 * fusión no estrenó ninguna URL (ADR-2026-08-17-analisis-datos-tabs-por-ruta).
 *
 * Reparto de encabezado: el título y el banner de vista de ejemplo viven acá
 * arriba UNA sola vez — las pantallas hijas se los sacaron. Si no, entrar por
 * cualquiera de las dos mostraba dos títulos y dos banners apilados.
 *
 * Las pestañas son links de verdad (`<Link>`, no botones): se pueden abrir en
 * otra pestaña del navegador, copiar la dirección y volver con el botón
 * "atrás". Encima se les monta el patrón `tablist` de WAI-ARIA — `tabIndex`
 * rotativo y flechas — para que el teclado las recorra como pestañas.
 */

export const AnalisisDatosPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activa = tabActivaSegunRuta(location.pathname);

  // Las dos anclas están siempre montadas, así que después de navegar se puede
  // devolver el foco a la que quedó activa sin esperar a ningún re-render.
  const anclas = useRef<Record<string, HTMLAnchorElement | null>>({});

  const irA = (tab: TabAnalisis) => {
    navigate(tab.ruta);
    anclas.current[tab.id]?.focus();
  };

  const alTeclado = (evento: KeyboardEvent<HTMLAnchorElement>) => {
    let destino: TabAnalisis | null = null;
    if (evento.key === "ArrowRight") destino = tabPorDesplazamiento(activa.id, 1);
    else if (evento.key === "ArrowLeft") destino = tabPorDesplazamiento(activa.id, -1);
    else if (evento.key === "Home") destino = TABS_ANALISIS[0];
    else if (evento.key === "End") destino = TABS_ANALISIS[TABS_ANALISIS.length - 1];
    if (!destino) return;
    evento.preventDefault();
    irA(destino);
  };

  return (
    <div className="p-8">
      <BannerVistaEjemplo />

      {/* El botón comparte fila con el título — no con las pestañas: ahí abajo
          competiría con el `tablist` y confundiría la navegación. */}
      <div className="mt-4 flex items-center justify-between gap-4">
        <h1 className="min-w-0 text-[22px] font-semibold text-brand-black">Análisis de datos</h1>
        <BotonPreguntaleAtlas
          pantalla="Análisis de datos del estudio de marketing"
          detalle={`Pestaña "${activa.etiqueta}".`}
        />
      </div>

      {/* Radios concéntricos: riel 12 = píldora 8 + 4 de padding (`p-1`). */}
      <div
        role="tablist"
        aria-label="Análisis de datos"
        className="mt-4 inline-flex gap-1 rounded-xl bg-[#F0F2F7] p-1"
      >
        {TABS_ANALISIS.map((tab) => {
          const esActiva = tab.id === activa.id;
          return (
            <Link
              key={tab.id}
              to={tab.ruta}
              id={`tab-${tab.id}`}
              role="tab"
              aria-selected={esActiva}
              aria-controls={`panel-${tab.id}`}
              tabIndex={esActiva ? 0 : -1}
              ref={(elemento) => {
                anclas.current[tab.id] = elemento;
              }}
              onKeyDown={alTeclado}
              className={`rounded-lg py-2 px-4 text-[13px] transition-colors ${
                // La activa no se distingue solo por el color: cambia de peso
                // tipográfico y se levanta en una superficie blanca con sombra
                // sobre el riel gris. Y lo dice `aria-selected` para quien no
                // ve ninguna de las dos cosas.
                esActiva
                  ? "bg-white font-semibold text-brand-primary shadow-[0_1px_2px_rgb(var(--brand-black-rgb)_/_0.10)]"
                  : "font-medium text-[rgb(var(--brand-black-rgb)_/_0.6)] hover:text-brand-black"
              }`}
            >
              {tab.etiqueta}
            </Link>
          );
        })}
      </div>

      {/* `tabIndex={0}`: ninguna de las dos pantallas tiene contenido enfocable,
          así que sin esto el teclado no llega nunca al panel. */}
      <div
        id={`panel-${activa.id}`}
        role="tabpanel"
        aria-labelledby={`tab-${activa.id}`}
        tabIndex={0}
        className="mt-6 outline-none"
      >
        <Outlet />
      </div>
    </div>
  );
};

export default AnalisisDatosPage;
